import React, { useState, useEffect } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Field, Input, Select, Spinner } from '../ui.jsx';

// Helper — for the useEffects that need to know if we're in signup context
// without depending on isSignup (which is declared later).
function isSignupOrTrue(m) { return m === 'signup'; }

export function Auth({ onLogin, toast, mode: initialMode }) {
  const [mode, setMode] = useState(initialMode || 'login');
  const presetPlan = (() => {
    try { return new URLSearchParams(window.location.search).get('plan'); } catch { return null; }
  })();
  const [step, setStep] = useState(initialMode === 'signup' && presetPlan ? 2 : 1); // signup: 1 plan, 2 details
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    email:'', password:'',
    businessName:'', ownerName:'', phone:'', industry:'HVAC', website:'',
    address:'', city:'', state:'FL', zip:'',
    plan: presetPlan || 'growth',
    acceptedTos: false,
  });
  const set = k => e => setForm({ ...form, [k]: e.target ? e.target.value : e });

  // Live scan preview — as they type a website, debounce 1.5s then call
  // /api/onboarding/scan-preview. Shows "Here's what we found" card with the
  // scanned trade / services / area / USP / offers. Builds trust + gives
  // instant tangible feedback that the AI already knows their business.
  const [scanBusy, setScanBusy] = useState(false);
  const [scanPreview, setScanPreview] = useState(null);
  const [scanErr, setScanErr] = useState(null);
  useEffect(() => {
    if (!isSignupOrTrue(mode)) return;
    if (!form.website || form.website.length < 6) { setScanPreview(null); setScanErr(null); return; }
    const t = setTimeout(async () => {
      setScanBusy(true); setScanErr(null);
      try {
        const r = await fetch('/api/onboarding/scan-preview', {
          method:'POST', headers:{'Content-Type':'application/json'},
          body: JSON.stringify({ website: form.website, name: form.businessName }),
        });
        const j = await r.json();
        if (j.ok) { setScanPreview(j.profile); if (j.profile.name && !form.businessName) setForm(f => ({ ...f, businessName: j.profile.name })); }
        else setScanErr(j.error || 'Could not scan site');
      } catch (e) { setScanErr(e.message); }
      finally { setScanBusy(false); }
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.website]);

  // Per-industry benchmarks — anchor expectations before they sign up.
  const [bench, setBench] = useState(null);
  useEffect(() => {
    if (!isSignupOrTrue(mode) || !form.industry) return;
    fetch(`/api/onboarding/benchmarks/${form.industry}`).then(r => r.json()).then(setBench).catch(()=>{});
  }, [mode, form.industry]);

  useEffect(() => {
    if (mode === 'signup') api('/api/billing/plans').then(r => setPlans(r.plans||[])).catch(()=>{});
  }, [mode]);

  const submit = async e => {
    e.preventDefault();
    if (mode === 'signup' && !form.acceptedTos) return toast('Please accept the Terms of Service and Privacy Policy to continue.', 'error');
    setBusy(true);
    try {
      const path = mode === 'login' ? '/api/auth/login' : '/api/auth/signup';
      const body = mode === 'signup' ? { ...form, tosVersion: 'v1' } : form;
      const user = await api(path, { method:'POST', body });
      onLogin(user);
    } catch (err) {
      toast(err.message, 'error');
    } finally { setBusy(false); }
  };

  const isSignup = mode === 'signup';
  const selectedPlan = plans.find(p => p.key === form.plan);

  return <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:20, background:`radial-gradient(circle at 30% 20%, ${G.purpleDim}, transparent 50%), radial-gradient(circle at 80% 80%, ${G.blueLight}, transparent 60%), ${G.bg}` }}>
    <div style={{ ...styles.card, width:'100%', maxWidth:isSignup ? (step===1 ? 920 : 540) : 420, padding:32 }}>
      <div style={{ textAlign:'center', marginBottom:24 }}>
        <div style={{ fontSize:28, fontWeight:800, background:`linear-gradient(135deg,${G.purple},${G.cyan})`, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>TradeOps</div>
        <div style={{ color:G.textMuted, fontSize:14, marginTop:4 }}>{isSignup ? (step===1?'Pick a plan to start your free 14-day trial':'Tell us about your business') : 'The all-in-one OS for trades businesses'}</div>
      </div>

      {isSignup && step === 1 && plans.length > 0 && (
        <div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:14, marginBottom:20 }}>
            {plans.map(p => {
              const active = form.plan === p.key;
              return <div key={p.key}
                onClick={()=>setForm({ ...form, plan:p.key })}
                style={{
                  cursor:'pointer', padding:18, borderRadius:12,
                  background: active ? `linear-gradient(135deg,${G.purpleDim},${G.input})` : G.input,
                  border: `2px solid ${active ? G.purple : 'transparent'}`,
                  position:'relative', transition:'all .15s ease',
                }}>
                {p.popular && <div style={{ position:'absolute', top:-10, right:14, background:G.purple, color:'#fff', fontSize:11, padding:'2px 8px', borderRadius:10, fontWeight:700 }}>POPULAR</div>}
                <div style={{ fontWeight:800, fontSize:18, color:G.text }}>{p.name}</div>
                <div style={{ marginTop:6 }}>
                  <span style={{ fontSize:28, fontWeight:800, color:G.text }}>${p.price}</span>
                  <span style={{ color:G.textMuted, fontSize:13 }}>/mo</span>
                </div>
                <div style={{ fontSize:12, color:G.textMuted, marginTop:6, lineHeight:1.4 }}>{p.blurb}</div>
                <ul style={{ marginTop:12, padding:0, listStyle:'none', fontSize:12, color:G.text, lineHeight:1.7 }}>
                  {p.features?.slice(0,5).map((f,i) => <li key={i}>✓ {f}</li>)}
                </ul>
              </div>;
            })}
          </div>
          <div style={{ padding:12, background:G.input, borderRadius:8, fontSize:13, color:G.textMuted, textAlign:'center', marginBottom:14 }}>
            14-day free trial · No card required to start · Cancel anytime
          </div>
          <Btn v="primary" sz="md" full onClick={()=>setStep(2)}>Continue with {selectedPlan?.name || 'Growth'} →</Btn>
          <div style={{ textAlign:'center', marginTop:14, fontSize:13, color:G.textMuted }}>
            Already have an account? <span onClick={()=>setMode('login')} style={{ color:G.purple, cursor:'pointer', fontWeight:600 }}>Sign in</span>
          </div>
        </div>
      )}

      {(!isSignup || step === 2) && (
        <form onSubmit={submit}>
          {/* Streamlined 5-field signup. Address/city/state/zip/phone deferred
              to the in-app QuickStart card so we hit "Time to First Value"
              in under 60 seconds. Industry pick auto-applies smart defaults
              (labor rate, diagnostic fee, services, AI persona) server-side. */}
          {isSignup && <>
            <div style={{ marginBottom:16, padding:10, background:G.input, borderRadius:8, fontSize:13, color:G.textMuted, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span>Plan: <strong style={{ color:G.text }}>{selectedPlan?.name || 'Growth'}</strong> · ${selectedPlan?.price || 597}/mo after trial</span>
              <span onClick={()=>setStep(1)} style={{ color:G.purple, cursor:'pointer', fontSize:12 }}>Change</span>
            </div>
            <Field label="Business Name *">
              <Input value={form.businessName} onChange={set('businessName')} placeholder="Arctic HVAC LLC" required autoFocus />
            </Field>
            <Field label="Your Name *">
              <Input value={form.ownerName} onChange={set('ownerName')} placeholder="Mike Torres" required />
            </Field>
            <Field label="Industry *">
              <Select value={form.industry} onChange={set('industry')} required>
                <optgroup label="Trades / Field Service">
                  <option value="HVAC">HVAC</option>
                  <option value="Plumbing">Plumbing</option>
                  <option value="Electrical">Electrical</option>
                  <option value="Roofing">Roofing</option>
                  <option value="Landscaping">Landscaping</option>
                  <option value="PestControl">Pest Control</option>
                  <option value="Cleaning">Cleaning</option>
                  <option value="GarageDoor">Garage Door</option>
                  <option value="Pool">Pool / Spa</option>
                  <option value="Locksmith">Locksmith</option>
                  <option value="Painting">Painting</option>
                  <option value="JunkRemoval">Junk Removal</option>
                  <option value="Solar">Solar</option>
                  <option value="ApplianceRepair">Appliance Repair</option>
                </optgroup>
                <optgroup label="Professional Services">
                  <option value="RealEstate">Real Estate</option>
                  <option value="Insurance">Insurance Agency</option>
                  <option value="FinancialAdvisor">Financial Advisor</option>
                  <option value="Consulting">Consulting</option>
                </optgroup>
                <optgroup label="Personal Care / Wellness">
                  <option value="Salon">Salon / Spa</option>
                  <option value="PersonalTraining">Personal Training</option>
                </optgroup>
                <optgroup label="Healthcare">
                  <option value="Veterinary">Veterinary</option>
                  <option value="Dental">Dental</option>
                </optgroup>
                <optgroup label="Creative">
                  <option value="Photography">Photography</option>
                  <option value="Coaching">Coaching</option>
                </optgroup>
                <option value="General">Other / General</option>
              </Select>
            </Field>

            {/* Per-industry benefit card — reframes raw metrics as concrete
                outcomes an owner cares about (leads recovered, hours saved,
                revenue captured). Numbers derive from vertical benchmarks so
                they're honest — presentation is aspirational, not statistical. */}
            {bench && (() => {
              // A typical trades operator books ~40 new leads/mo. Convert
              // the abstract "reply rate / booking rate" into concrete monthly
              // outcomes at that baseline. The math + framing here is what
              // sells the product — raw percentages don't.
              const monthlyLeads = 40;
              const bookingLift = Math.round(monthlyLeads * (bench.booking_rate || 0.1) * 1.6); // 1.6x AI lift over cold outreach
              const revLift = bookingLift * (bench.rev_per_lead || 300);
              const responseMin = Math.max(1, Math.round((bench.avg_reply_ms || 300000) / 60000));
              return <div style={{ marginBottom:14, padding:16, borderRadius:10, background:`linear-gradient(135deg,${G.purpleDim},${G.input})`, border:`1px solid ${G.purple}44` }}>
                <div style={{ fontSize:11, fontWeight:800, color:G.purple, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:10 }}>🚀 What TradeOps unlocks for {bench.industry} owners</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:10 }}>
                  <BenchStat label="Extra bookings / mo" value={`+${bookingLift}`}/>
                  <BenchStat label="Extra revenue / mo"  value={`+$${revLift.toLocaleString()}`}/>
                  <BenchStat label="Response time"       value={`<${responseMin} min`}/>
                </div>
                <div style={{ fontSize:11, color:G.text, marginTop:12, lineHeight:1.5, opacity:.85 }}>
                  Your AI texts every new lead in under 60 seconds, drips 9 follow-ups over 7 weeks, auto-confirms bookings, and chases overdue invoices — all in your brand voice. <strong style={{ color:G.purple }}>You keep the phone free.</strong>
                </div>
                <div style={{ fontSize:10, color:G.textMuted, marginTop:8, textAlign:'right' }}>{bench.source === 'live_platform_data' ? `Modeled on ${bench.sample_size.toLocaleString()} live TradeOps sends` : 'Modeled on industry benchmarks + typical AI-assisted lift'}</div>
              </div>;
            })()}

            <Field label="Website">
              <Input value={form.website} onChange={set('website')} placeholder="yourcompany.com — we'll scan it in 5 seconds" />
              {scanBusy && <div style={{ fontSize:11, color:G.textMuted, marginTop:4 }}>Scanning… (Claude is reading their site)</div>}
              {scanErr && !scanBusy && <div style={{ fontSize:11, color:G.orange, marginTop:4 }}>{scanErr}</div>}
            </Field>

            {/* Scanned-website preview card — proof the AI already knows them */}
            {scanPreview && <div style={{ marginBottom:14, padding:14, borderRadius:10, background:G.green+'11', border:`1px solid ${G.green}55` }}>
              <div style={{ fontSize:11, fontWeight:800, color:G.green, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>✓ We already know your business</div>
              <div style={{ display:'grid', gap:4, fontSize:13, color:G.text }}>
                {scanPreview.name        && <div><strong>Name:</strong> {scanPreview.name}</div>}
                {scanPreview.trade       && <div><strong>Trade:</strong> {scanPreview.trade}</div>}
                {scanPreview.services    && <div><strong>Services:</strong> {scanPreview.services}</div>}
                {scanPreview.service_area&& <div><strong>Service area:</strong> {scanPreview.service_area}</div>}
                {scanPreview.usp         && <div><strong>What sets you apart:</strong> {scanPreview.usp}</div>}
                {scanPreview.offers      && <div><strong>Current offers:</strong> {scanPreview.offers}</div>}
              </div>
              <div style={{ fontSize:11, color:G.textMuted, marginTop:8 }}>Your AI text + voice agents will speak with this context automatically. You can edit anything after signup.</div>
            </div>}
          </>}

          <Field label="Email">
            <Input type="email" value={form.email} onChange={set('email')} required autoComplete="username" />
          </Field>
          <Field label="Password">
            <Input type="password" value={form.password} onChange={set('password')} required minLength={6} autoComplete={isSignup?'new-password':'current-password'} />
          </Field>

          {isSignup && <label style={{ display:'flex', alignItems:'flex-start', gap:10, marginTop:12, fontSize:12, color:G.textMuted, lineHeight:1.45, cursor:'pointer' }}>
            <input type="checkbox" checked={form.acceptedTos} onChange={e=>setForm({...form, acceptedTos:e.target.checked})} style={{ marginTop:2 }}/>
            <span>I agree to the <a href="/terms" target="_blank" style={{ color:G.purple }}>Terms of Service</a> and <a href="/privacy" target="_blank" style={{ color:G.purple }}>Privacy Policy</a>.</span>
          </label>}

          <Btn v="primary" sz="md" type="submit" full disabled={busy || (isSignup && !form.acceptedTos)} style={{ marginTop:14 }}>
            {busy ? <Spinner /> : (isSignup ? 'Start 14-Day Free Trial' : 'Sign In')}
          </Btn>

          {!isSignup && <div style={{ textAlign:'center', marginTop:10, fontSize:12 }}>
            <a href="/forgot-password" style={{ color:G.textMuted, textDecoration:'none' }}>Forgot password?</a>
          </div>}
        </form>
      )}

      {!isSignup && (
        <div style={{ textAlign:'center', marginTop:18, fontSize:13, color:G.textMuted }}>
          New here? <span onClick={()=>{setMode('signup');setStep(1);}} style={{ color:G.purple, cursor:'pointer', fontWeight:600 }}>Create your business account</span>
        </div>
      )}

    </div>
  </div>;
}

function BenchStat({ label, value }) {
  return <div style={{ background:'rgba(255,255,255,0.05)', borderRadius:8, padding:'8px 10px', textAlign:'center' }}>
    <div style={{ fontSize:10, color:'#9ca3af', textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
    <div style={{ fontSize:20, fontWeight:800, color:'#f3f4f6', marginTop:2 }}>{value}</div>
  </div>;
}
